import { useMutation } from '@apollo/client'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Button, ButtonColor, NotificationType } from '../../../components'
import { UPDATE_CREATOR } from '../../../gql'
import {
  ChildAgeRange,
  Creator,
  CreatorPetsOwner,
  MutationUpdateCreatorArgs,
} from '../../../gql/types'
import { useNotification } from '../../../utils/hooks'
import {
  childAgeRangeLocalizedString,
  childAgeRangeLocalizedStringInverse,
  petLocalizedString,
  petLocalizedStringInverse,
} from '../../../utils/GqlStrings'
import Multiselect from '../../../elements/Multiselect'

interface IProps {
  creator: Creator
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  setHasPetsAndChildren?: (value: boolean) => void
}

type FormData = {
  petsOwner: string[]
  childAgeRanges: string[]
}

const SetPetsAndChildrenModal = ({
  creator,
  isOpen,
  setIsOpen,
  setHasPetsAndChildren,
}: IProps) => {
  const { addNotification } = useNotification()
  const [updateCreator, { loading }] =
    useMutation<MutationUpdateCreatorArgs>(UPDATE_CREATOR)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      petsOwner: creator.petsOwner?.map(petLocalizedString) || [],
      childAgeRanges:
        creator.childAgeRanges?.map(childAgeRangeLocalizedString) || [],
    },
  })

  const childAgeRanges = watch('childAgeRanges')
  const petsOwner = watch('petsOwner')

  useEffect(() => {
    if (!childAgeRanges) return

    const hasNoChildren = childAgeRanges.includes(
      childAgeRangeLocalizedString(ChildAgeRange.noChildren),
    )
    const hasOtherOptions = childAgeRanges.some(
      (range) =>
        range !== childAgeRangeLocalizedString(ChildAgeRange.noChildren),
    )

    if (hasNoChildren && hasOtherOptions) {
      // If both "No Children" and other options are selected, keep only the last selected option
      const lastSelected = childAgeRanges[childAgeRanges.length - 1]
      if (
        lastSelected === childAgeRangeLocalizedString(ChildAgeRange.noChildren)
      ) {
        setValue('childAgeRanges', [lastSelected])
      }
      if (
        lastSelected !== childAgeRangeLocalizedString(ChildAgeRange.noChildren)
      ) {
        setValue(
          'childAgeRanges',
          childAgeRanges.filter(
            (range) =>
              range !== childAgeRangeLocalizedString(ChildAgeRange.noChildren),
          ),
        )
      }
    }
  }, [childAgeRanges, setValue])

  useEffect(() => {
    if (!petsOwner) return

    const hasNoPets = petsOwner.includes(
      petLocalizedString(CreatorPetsOwner.noPets),
    )
    const hasOtherOptions = petsOwner.some(
      (pet) => pet !== petLocalizedString(CreatorPetsOwner.noPets),
    )

    if (hasNoPets && hasOtherOptions) {
      // If both "No Pets" and other options are selected, keep only the last selected option
      const lastSelected = petsOwner[petsOwner.length - 1]
      if (lastSelected === petLocalizedString(CreatorPetsOwner.noPets)) {
        setValue('petsOwner', [lastSelected])
      }
      if (lastSelected !== petLocalizedString(CreatorPetsOwner.noPets)) {
        setValue(
          'petsOwner',
          petsOwner.filter(
            (pet) => pet !== petLocalizedString(CreatorPetsOwner.noPets),
          ),
        )
      }
    }
  }, [petsOwner, setValue])

  const closeModal = () => {
    setIsOpen(false)
    reset()
  }

  const onSubmit = async (data: FormData) => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            petsOwner:
              data.petsOwner && data.petsOwner.length > 0
                ? data.petsOwner.map((petOwner) =>
                    petLocalizedStringInverse(petOwner),
                  )
                : null,
            childAgeRanges:
              data.childAgeRanges && data.childAgeRanges.length > 0
                ? data.childAgeRanges.map((childAgeRange) =>
                    childAgeRangeLocalizedStringInverse(childAgeRange),
                  )
                : null,
          },
        },
      })

      // Update the parent component state
      if (setHasPetsAndChildren) {
        // Update only if both petsOwner and childAgeRanges exist
        const hasPetsAndChildrenData =
          data.petsOwner?.length > 0 && data.childAgeRanges?.length > 0

        setHasPetsAndChildren(hasPetsAndChildrenData)
      }

      addNotification('Profile updated successfully', NotificationType.success)
      closeModal()
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-10' onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black bg-opacity-25' />
        </Transition.Child>

        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4 text-center'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all'>
                <Dialog.Title
                  as='h3'
                  className='text-lg font-medium leading-6 text-gray-900'
                >
                  Children & Pets Information
                </Dialog.Title>
                <div className='mt-4'>
                  <p className='text-sm text-gray-500'>
                    Sharing your children and pets details helps us match you
                    with more relevant campaigns and opportunities that align
                    with your lifestyle.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className='mt-6 space-y-6'
                >
                  <div>
                    <Controller
                      name='petsOwner'
                      control={control}
                      rules={{
                        required: `Please select at least one option.`,
                      }}
                      render={({ field }) => {
                        const options = Object.values(CreatorPetsOwner).map(
                          (petsOwner) => ({
                            value: petsOwner,
                            label: petLocalizedString(petsOwner),
                          }),
                        )

                        const selectedOptions =
                          field.value?.map((petsOwner) => ({
                            value: petLocalizedStringInverse(petsOwner),
                            label: petsOwner,
                          })) || []

                        const handleChange = (selected) => {
                          if (!selected || selected.length === 0) {
                            field.onChange([])
                            return
                          }

                          field.onChange(selected.map((option) => option.label))
                        }

                        return (
                          <div className='w-full'>
                            <label className='block text-sm leading-5 font-medium text-gray-700'>
                              Do you have any pets?
                            </label>
                            <div className='mt-1'>
                              <Multiselect
                                options={options}
                                selected={selectedOptions}
                                onChange={handleChange}
                                placeholder='Select your pets...'
                                menuPlacement='top'
                              />
                            </div>
                            {errors?.petsOwner?.message && (
                              <p className='mt-2 text-sm text-red-600'>
                                {errors.petsOwner.message}
                              </p>
                            )}
                          </div>
                        )
                      }}
                    />
                  </div>

                  <div>
                    <Controller
                      name='childAgeRanges'
                      control={control}
                      rules={{
                        required: `Please select at least one option.`,
                      }}
                      render={({ field }) => {
                        const options = Object.values(ChildAgeRange).map(
                          (childAgeRange) => ({
                            value: childAgeRange,
                            label: childAgeRangeLocalizedString(childAgeRange),
                          }),
                        )

                        const selectedOptions =
                          field.value?.map((childAgeRange) => ({
                            value:
                              childAgeRangeLocalizedStringInverse(
                                childAgeRange,
                              ),
                            label: childAgeRange,
                          })) || []

                        const handleChange = (selected) => {
                          if (!selected || selected.length === 0) {
                            field.onChange([])
                            return
                          }
                          field.onChange(selected.map((option) => option.label))
                        }

                        return (
                          <div className='w-full'>
                            <label className='block text-sm leading-5 font-medium text-gray-700'>
                              Do you have children? If yes, what are their age
                              ranges?
                            </label>
                            <div className='mt-1'>
                              <Multiselect
                                options={options}
                                selected={selectedOptions}
                                onChange={handleChange}
                                placeholder='Select age ranges...'
                                menuPlacement='top'
                              />
                            </div>
                            {errors?.childAgeRanges?.message && (
                              <p className='mt-2 text-sm text-red-600'>
                                {errors.childAgeRanges.message}
                              </p>
                            )}
                          </div>
                        )
                      }}
                    />
                  </div>

                  <div className='mt-6 flex justify-end space-x-3'>
                    <Button
                      title='Cancel'
                      type='button'
                      colour={ButtonColor.lightGray}
                      onClick={closeModal}
                    />
                    <Button
                      title='Save'
                      type='submit'
                      colour={ButtonColor.primary}
                      loading={loading}
                    />
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export { SetPetsAndChildrenModal }
